
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, User, Phone, Mail, Lock, Building, Languages, CheckCircle, AlertCircle, Bell } from 'lucide-react';
import DoctorHeader from '@/components/doctor/DoctorHeader';
import DoctorFooterNav from '@/components/doctor/DoctorFooterNav';

const specialties = [
  "General Dentistry",
  "Orthodontics",
  "Endodontics",
  "Periodontics",
  "Prosthodontics",
  "Oral Surgery",
  "Pediatric Dentistry",
  "Cosmetic Dentistry"
];

const availableLanguages = [
  { id: "en", name: "English" },
  { id: "es", name: "Spanish" },
  { id: "fr", name: "French" },
  { id: "de", name: "German" },
  { id: "tr", name: "Turkish" },
  { id: "ar", name: "Arabic" },
  { id: "zh", name: "Chinese" },
  { id: "ru", name: "Russian" },
  { id: "ja", name: "Japanese" },
  { id: "ko", name: "Korean" }
];

const DoctorSettings = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Personal info state
  const [fullName, setFullName] = useState("Dr. Sarah Johnson");
  const [email] = useState("sarah.johnson@example.com");
  const [phone, setPhone] = useState("+1 (555) 123-4567");
  const [bio, setBio] = useState("Board-certified dentist with over 10 years of experience specializing in cosmetic and restorative dentistry.");
  
  // Professional info state
  const [specialty, setSpecialty] = useState("Cosmetic Dentistry");
  const [yearsExperience, setYearsExperience] = useState("12");
  const [selectedLanguages, setSelectedLanguages] = useState(["en", "es", "fr"]);
  const [translationEnabled, setTranslationEnabled] = useState(true);
  
  // Clinic info state
  const [clinicName] = useState("Bright Smile Dental Clinic");
  
  // Notification preferences state
  const [appointmentReminders, setAppointmentReminders] = useState(true);
  const [reviewNotifications, setReviewNotifications] = useState(true);
  const [messageNotifications, setMessageNotifications] = useState(true);
  const [platformUpdates, setPlatformUpdates] = useState(false);
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // Form state
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  
  // Handle form submission
  const handleSave = () => {
    setSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      setSaving(false);
      toast({
        title: "Settings saved",
        description: "Your profile settings have been updated successfully.",
      });
    }, 1000);
  };
  
  // Handle password change
  const handlePasswordChange = () => {
    if (!currentPassword) {
      toast({
        title: "Current password required",
        description: "Please enter your current password.",
        variant: "destructive",
      });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "New password and confirmation must match.",
        variant: "destructive",
      });
      return;
    }
    
    if (newPassword.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive",
      });
      return;
    }
    
    setSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      setSaving(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast({
        title: "Password updated",
        description: "Your password has been changed successfully.",
      });
    }, 1000);
  };
  
  // Handle language selection
  const toggleLanguage = (languageId: string) => {
    if (selectedLanguages.includes(languageId)) {
      setSelectedLanguages(selectedLanguages.filter(id => id !== languageId));
    } else {
      setSelectedLanguages([...selectedLanguages, languageId]);
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      <DoctorHeader />
      
      <div className="container mx-auto p-4 pt-20 pb-28">
        <div className="flex items-center gap-2 mb-6">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="mr-1"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Profile & Settings</h1>
        </div>
        
        <Tabs defaultValue="personal" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="personal">Personal Info</TabsTrigger>
            <TabsTrigger value="professional">Professional Info</TabsTrigger>
            <TabsTrigger value="clinic">Clinic Affiliation</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
          </TabsList>
          
          <TabsContent value="personal">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>
                  Manage your personal details and account security
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <div className="flex items-center space-x-2">
                    <User className="w-5 h-5 text-muted-foreground" />
                    <Input 
                      id="fullName" 
                      value={fullName} 
                      onChange={(e) => setFullName(e.target.value)} 
                      placeholder="Your full name"
                      className="flex-1"
                    />
                  </div>
                </div>
                
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="flex items-center space-x-2">
                    <Mail className="w-5 h-5 text-muted-foreground" />
                    <Input 
                      id="email" 
                      value={email} 
                      readOnly 
                      disabled
                      className="flex-1 bg-muted cursor-not-allowed"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Contact support to change your email address
                  </p>
                </div>
                
                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="flex items-center space-x-2">
                    <Phone className="w-5 h-5 text-muted-foreground" />
                    <Input 
                      id="phone" 
                      value={phone} 
                      onChange={(e) => setPhone(e.target.value)} 
                      placeholder="Your phone number"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Verification may be required when changing your phone number
                  </p>
                </div>
                
                {/* Bio */}
                <div className="space-y-2">
                  <Label htmlFor="bio">Professional Bio</Label>
                  <Textarea 
                    id="bio" 
                    value={bio} 
                    onChange={(e) => setBio(e.target.value)} 
                    placeholder="Tell patients about yourself"
                    rows={4}
                  />
                </div>
                
                {/* Password Change */}
                <div className="pt-4 border-t">
                  <h3 className="text-lg font-medium mb-4 flex items-center">
                    <Lock className="w-5 h-5 mr-2" />
                    Change Password
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword">Current Password</Label>
                      <Input 
                        id="currentPassword" 
                        type="password" 
                        value={currentPassword} 
                        onChange={(e) => setCurrentPassword(e.target.value)} 
                        placeholder="Enter current password"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">New Password</Label>
                      <Input 
                        id="newPassword" 
                        type="password" 
                        value={newPassword} 
                        onChange={(e) => setNewPassword(e.target.value)} 
                        placeholder="Enter new password"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm New Password</Label>
                      <Input 
                        id="confirmPassword" 
                        type="password" 
                        value={confirmPassword} 
                        onChange={(e) => setConfirmPassword(e.target.value)} 
                        placeholder="Confirm new password"
                      />
                    </div>
                    
                    <Button 
                      onClick={handlePasswordChange} 
                      disabled={saving}
                    >
                      {saving ? "Updating..." : "Update Password"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="professional">
            <Card>
              <CardHeader>
                <CardTitle>Professional Information</CardTitle>
                <CardDescription>
                  Update your professional details and capabilities
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Specialty */}
                <div className="space-y-2">
                  <Label htmlFor="specialty">Specialty</Label>
                  <Select 
                    value={specialty} 
                    onValueChange={setSpecialty}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your specialty" />
                    </SelectTrigger>
                    <SelectContent>
                      {specialties.map((specialtyOption) => (
                        <SelectItem key={specialtyOption} value={specialtyOption}>
                          {specialtyOption}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Years of Experience */}
                <div className="space-y-2">
                  <Label htmlFor="yearsExperience">Years of Experience</Label>
                  <Input 
                    id="yearsExperience" 
                    type="number" 
                    min="0"
                    value={yearsExperience} 
                    onChange={(e) => setYearsExperience(e.target.value)} 
                    placeholder="Years of professional experience"
                  />
                </div>
                
                {/* Spoken Languages */}
                <div className="space-y-2">
                  <Label className="block mb-2">Spoken Languages</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {availableLanguages.map((language) => (
                      <div 
                        key={language.id}
                        className="flex items-center space-x-2"
                      >
                        <input 
                          type="checkbox" 
                          id={`lang-${language.id}`} 
                          checked={selectedLanguages.includes(language.id)} 
                          onChange={() => toggleLanguage(language.id)}
                          className="rounded"
                        />
                        <label 
                          htmlFor={`lang-${language.id}`}
                          className="text-sm"
                        >
                          {language.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Translation */}
                <div className="space-y-2 pt-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="translationEnabled">Enable Instant Translation</Label>
                      <p className="text-sm text-muted-foreground">
                        Allow automatic translation of patient messages
                      </p>
                    </div>
                    <Switch 
                      id="translationEnabled" 
                      checked={translationEnabled} 
                      onCheckedChange={setTranslationEnabled}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="clinic">
            <Card>
              <CardHeader>
                <CardTitle>Clinic Affiliation</CardTitle>
                <CardDescription>
                  View your current clinic affiliation details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-start space-x-4">
                    <Building className="w-10 h-10 text-primary mt-1" />
                    <div>
                      <h3 className="text-lg font-medium">{clinicName}</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Your account is affiliated with this clinic. Changes to clinic affiliation require administrator approval.
                      </p>
                      <div className="mt-4 flex items-center text-sm text-muted-foreground">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        To request a change in clinic affiliation, please contact your clinic administrator.
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>
                  Control how and when you receive notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  {/* Appointment Reminders */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="appointmentReminders">Appointment Reminders</Label>
                      <p className="text-sm text-muted-foreground">
                        Receive notifications about upcoming appointments
                      </p>
                    </div>
                    <Switch 
                      id="appointmentReminders" 
                      checked={appointmentReminders} 
                      onCheckedChange={setAppointmentReminders}
                    />
                  </div>
                  
                  {/* New Reviews */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="reviewNotifications">New Reviews</Label>
                      <p className="text-sm text-muted-foreground">
                        Get notified when patients leave new reviews
                      </p>
                    </div>
                    <Switch 
                      id="reviewNotifications" 
                      checked={reviewNotifications} 
                      onCheckedChange={setReviewNotifications}
                    />
                  </div>
                  
                  {/* New Messages */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="messageNotifications">New Messages</Label>
                      <p className="text-sm text-muted-foreground">
                        Get notified when you receive new messages
                      </p>
                    </div>
                    <Switch 
                      id="messageNotifications" 
                      checked={messageNotifications} 
                      onCheckedChange={setMessageNotifications}
                    />
                  </div>
                  
                  {/* Platform Updates */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="platformUpdates">Platform Updates</Label>
                      <p className="text-sm text-muted-foreground">
                        Receive information about new features and updates
                      </p>
                    </div>
                    <Switch 
                      id="platformUpdates" 
                      checked={platformUpdates} 
                      onCheckedChange={setPlatformUpdates}
                    />
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <div className="flex items-start space-x-4">
                    <Bell className="w-5 h-5 text-primary mt-1" />
                    <p className="text-sm text-muted-foreground">
                      Notifications will be sent to your registered email address and through the platform. You can also manage your account notifications in your email provider.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        <div className="mt-6 flex justify-end">
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="px-6"
          >
            {saving ? (
              "Saving Changes..."
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
      
      <DoctorFooterNav />
    </div>
  );
};

export default DoctorSettings;
